import { useState } from 'react';

/**
 * Sets/gets a value from local storage. The type can generally be inferred.
 *
 * Examples:
 * const [val, setVal] = useLocalStorage('count', 1)
 * setVal(2);    // allowed, because the type of `1` is number
 * setVal('two') // error, because `'two'` is not of type number
 *
 * const [val, setVal] = useLocalStorage('nameList', []);
 * setVal('justin'); // errors because type inferred to be `never[]`
 * fix with: const [val, setVal] = useLocalStorage<string[]>('nameList', []);
 *
 * type AllowedCounts = 1 | 2 | 3 | 4; // create a type with a narrower range
 * const [val, setVal] = useLocalStorage<AllowedCounts>('count', 1)
 * setVal(2);    // allowed, because `2` is an AllowedCount
 * setVal(5) // error, because 5 is out of range
 *
 */
export const useLocalStorage = <StoredValueType>(
  key: string,
  initialValue: StoredValueType,
): [StoredValueType, (arg: StoredValueType) => void] => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<StoredValueType>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);

      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage.
  const setValue = (value: StoredValueType): void => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  return [storedValue, setValue];
};
